class VanillaSticky {
  constructor(selector = ".js-move", options = {}, root = document) {
    this.selector = selector;
    this.root = root;

    this.strength = options.strength ?? 0.4;
    this.ease = options.ease ?? 0.16;
    this.maxMove = options.maxMove ?? 24;

    this.instances = [];

    this.init();
  }

  init() {
    const elements = this.getElements();

    if (!elements.length) return;

    elements.forEach((element) => {
      this.createInstance(element);
    });
  }

  getElements() {
    if (this.root && typeof this.root.querySelectorAll === "function") {
      return Array.from(this.root.querySelectorAll(this.selector));
    }

    return Array.from(document.querySelectorAll(this.selector));
  }

  createInstance(element) {
    const instance = {
      element,
      rect: null,

      currentX: 0,
      currentY: 0,
      targetX: 0,
      targetY: 0,

      rafId: null,
      isHover: false,

      handlePointerEnter: null,
      handlePointerMove: null,
      handlePointerLeave: null,
    };

    instance.handlePointerEnter = () => {
      instance.isHover = true;
      instance.rect = element.getBoundingClientRect();

      this.startAnimation(instance);
    };

    instance.handlePointerMove = (event) => {
      if (!instance.rect) {
        instance.rect = element.getBoundingClientRect();
      }

      const x =
        (event.clientX - instance.rect.left - instance.rect.width / 2) *
        this.strength;

      const y =
        (event.clientY - instance.rect.top - instance.rect.height / 2) *
        this.strength;

      instance.targetX = this.clamp(x, -this.maxMove, this.maxMove);
      instance.targetY = this.clamp(y, -this.maxMove, this.maxMove);

      this.startAnimation(instance);
    };

    instance.handlePointerLeave = () => {
      instance.isHover = false;
      instance.rect = null;

      instance.targetX = 0;
      instance.targetY = 0;

      this.startAnimation(instance);
    };

    element.addEventListener(
      "pointerenter",
      instance.handlePointerEnter,
      false,
    );

    element.addEventListener("pointermove", instance.handlePointerMove, false);

    element.addEventListener(
      "pointerleave",
      instance.handlePointerLeave,
      false,
    );

    this.instances.push(instance);
  }

  startAnimation(instance) {
    if (instance.rafId) return;

    instance.rafId = window.requestAnimationFrame(() => {
      this.animate(instance);
    });
  }

  animate(instance) {
    const { element } = instance;

    if (!element) return;

    instance.currentX += (instance.targetX - instance.currentX) * this.ease;
    instance.currentY += (instance.targetY - instance.currentY) * this.ease;

    const x = this.round(instance.currentX);
    const y = this.round(instance.currentY);

    element.style.transform = `translate3d(${x}px, ${y}px, 0)`;

    const isSettled =
      Math.abs(instance.targetX - instance.currentX) < 0.01 &&
      Math.abs(instance.targetY - instance.currentY) < 0.01;

    if (!instance.isHover && isSettled) {
      instance.currentX = 0;
      instance.currentY = 0;
      instance.targetX = 0;
      instance.targetY = 0;

      element.style.transform = "translate3d(0, 0, 0)";

      instance.rafId = null;
      return;
    }

    instance.rafId = window.requestAnimationFrame(() => {
      this.animate(instance);
    });
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  round(value) {
    return Math.round(value * 1000) / 1000;
  }

  destroy() {
    if (!this.instances.length) return;

    this.instances.forEach((instance) => {
      const { element } = instance;

      if (!element) return;

      if (instance.rafId) {
        window.cancelAnimationFrame(instance.rafId);
        instance.rafId = null;
      }

      element.removeEventListener(
        "pointerenter",
        instance.handlePointerEnter,
        false,
      );

      element.removeEventListener(
        "pointermove",
        instance.handlePointerMove,
        false,
      );

      element.removeEventListener(
        "pointerleave",
        instance.handlePointerLeave,
        false,
      );

      element.style.transform = "";
    });

    this.instances = [];
  }
}

export default VanillaSticky;
